import dayjs from "dayjs";
import { useSearchParams } from "next/navigation";
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import {
  Dropdown,
  DropdownItem,
  Modal,
  TextFieldSts,
  DateInput,
  InlineInfoBox,
} from "staysure-component-library";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import ButtonGroup from "@/components/atoms/button-group/button-group";
import InputValidationWrapper from "@/components/atoms/input-validation-wrapper/input-validation-wrapper";
import {
  AddButton,
  RemoveButton,
  TextButton,
} from "@/components/molecules/cta-button/cta-buttons";
import withBrand from "@/components/with-brand";
import {
  Couple,
  Family,
  Group,
  Individual,
  OrganizerEdit,
  QuestionSeven,
  QuestionThree,
  SingleParentFamily,
  TravellerEdit,
} from "@/config/app-constant";
import { ANNUAL_MULTI_TRIP, ERROR, WARNING } from "@/constants";
import { useFetchNationalityData } from "@/hooks/api/address-api/address-api";
import useGtmPopup from "@/hooks/gtm/use-gtm-popup";
import useInputFocus from "@/hooks/use-input-focus/use-input-focus";
import useMediaQuery from "@/hooks/use-media-query/use-media-query";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { RootState } from "@/store/store";
import { TitleKey } from "@/types/Organiser";
import {
  AgeLimitProps,
  FormDataType,
  TravellerFormProps,
} from "@/types/TravellerType";
import {
  findAnswerQThreeById,
  findAnswerQThreeByValue,
  getAnswersQuestionThree,
} from "@/utils/cruise.utils";
import { reorderNationalities } from "@/utils/filtered-nationality.utils";
import {
  getStrokeColor,
  validateIdentificationNumber,
  hidePersonalDetailsFields,
} from "@/utils/organiser.utils";
import { isValidDate } from "@/utils/travel-date.utils";
import {
  calculateTravellerAge,
  getDateValidation,
  getDateValidationStates,
  getFontClasses,
  getGroupValidate,
  getTravellerRole,
  isFamilyValidate,
  isFormDataComplete,
  isSingleFamilyValidate,
  useTranslatedTitleData,
  validateDate,
  validateDateGroup,
  validateName,
} from "@/utils/traveller.utils";

import Styles from "./traveller-form.module.css";
import NationalityStyles from "../../organizer/organiser-answer/organiser-form.module.css";
import TravellerMedicalAnswer from "../traveller-medical-answer/traveller-medical-answer";

const formDataInitialValue = {
  id: 1,
  value1: "",
  value2: "",
  value3: "",
  value4: "",
  nationalityValue: undefined,
  dateValue: undefined,
  isDateCalenter: true,
  // Identification fields (Spain only)
  identificationType: undefined,
  identificationNumber: undefined,
};

function QSixForm({
  inputOnePlaceholder,
  inputTwoPlaceholder,
  addButtonLabel,
  removeButtonLabel,
  formNo,
  multipleForm,
  handleSubmitButton,
  inputOneErrorNumericvalues,
  inputOneErrorSpecialCharacters,
  inputOneErrorMaximumNumber,
  inputTwoErrorNumericvalues,
  inputTwoErrorSpecialCharacters,
  inputTwoErrorMaximumNumber,
  dobMinAge,
  dobMaxAge,
  popupMessage,
  travellerType,
  coupleMinAge,
  personalStatusPlaceholder,
  nationalityPlaceholder,
  nationalityPopularCountryList,
  ageParentFamily,
  ageFamilyFullTimeEducation,
  ageGroupUnderTwo,
  ageSingleParentFamily,
  ageSingleParentFullTimeEducation,
  travellerDetailsEdit,
  errorObjEdited,
  dobPlaceholder,
  organizer,
  brand,
  newMedicalFlowEnabled,
  hasMedicalConditions,
  medicalQuestionText,
  medicalQuestionInfoText,
  medicalServiceDeniedText,
  medicalYesBtnText,
  medicalNoBtnText,
  travelersLimit,
  handleMedicalQuestion,
  clickedFormTitle,
  onTravellerDobValidationTrigger,
  // Identification field Magnolia content (Spain only)
  identificationTypePlaceholder,
  identificationNumberPlaceholder,
  identificationTypeRequiredError,
  identificationNumberInvalidError,
  identificationNumberWarning,
  identificationTypeDNILabel,
  identificationTypeNIELabel,
  identificationTypePassportLabel,
  travellerAlertMessageIncompleteSectionSingle,
  travellerAlertMessageIncompleteSection,
  travellerAlertMessageLinkSectionSingle,
  travellerAlertMessageLinkSection,
  showErrors,
  validationTrigger,
}: Readonly<TravellerFormProps>) {
  const [formData, setFormData] = useState<FormDataType[]>([
    formDataInitialValue,
  ]);
  const [firstNameErrors, setFirstNameErrors] = useState<string[]>([]);
  const [titleErrors, setTitleErrors] = useState<string[]>([]);
  const [lastNameErrors, setLastNameErrors] = useState<string[]>([]);
  const [ageErrors, setAgeErrors] = useState<string[]>([]);
  const [isEditingDob, setIsEditingDob] = useState<boolean[]>([]);
  // const [ages] = useState<number[]>(new Array(formData.length).fill(0));
  const [activeHoverd, setActiveHoverd] = useState<boolean>(true);
  const [inputDisable, setInputDisable] = useState<boolean>(false);
  const [scrollMedicalQuestion, setScrollMedicalQuestion] =
    useState<boolean>(false);
  const [isTitleFocused, setIsTitleFocused] = useState<boolean>(false);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [isTabPressed, setIsTabPressed] = useState(false);
  const isdesktop = useMediaQuery("(min-width: 600px)");
  const isLargeDesktop = useMediaQuery("(min-width: 1024px)");
  const inputRefs = useRef<(HTMLDivElement | null)[]>([]);
  const addTravellerRef = useRef<HTMLDivElement | null>(null);
  const [travellerAdded, setTravellerAdded] = useState(false);
  const [isNationalityFocused, setIsNationalityFocused] =
    useState<boolean>(false);
  const isSubmittingRef = useRef<boolean>(false);
  const lastCascadeCallRef = useRef<number>(0);
  const lastMarkedFieldRef = useRef<string>("");
  const isUpdatingFocusStateRef = useRef<boolean>(false);
  const prevMedicalConditionsRef = useRef<boolean | undefined>(
    hasMedicalConditions
  );
  const [validatedFormCount, setValidatedFormCount] = useState<number>(1);
  // Refs for identification fields (Spain only)
  const identificationTypeRefs = useRef<(HTMLDivElement | null)[]>([]);
  // State for identification dropdown focus (one per traveller)
  const [isIdentificationDropdownFocused, setIsIdentificationDropdownFocused] =
    useState<boolean[]>([]);

  const { current, wizard } = useWizard();
  const { t } = useWizardTranslation();
  const translatedAnswersQuestionThree = getAnswersQuestionThree(t);

  const titleData = useTranslatedTitleData(
    current?.brandSpecificParams?.excludedTitles as TitleKey[]
  );

  // Fetch nationality list data
  const nationalityDataList = useFetchNationalityData(
    current?.brandSpecificParams?.nationalityField as boolean
  );

  const nationalityUserTriggeredRef = useRef<boolean[]>([]);
  const previousNationalityRef = useRef<(string | undefined)[]>([]);

  const nationalityData = useMemo(() => {
    return reorderNationalities(
      nationalityDataList ?? [],
      nationalityPopularCountryList ?? []
    );
  }, [nationalityDataList, nationalityPopularCountryList]);

  // Identification types - memoized once for all travellers (moved from TravellerIdentificationFields)
  const allIdentificationTypes = useMemo(
    () => [
      {
        id: "DNI",
        label: identificationTypeDNILabel,
      },
      {
        id: "NIE",
        label: identificationTypeNIELabel,
      },
      {
        id: "PASSPORT",
        label: identificationTypePassportLabel,
      },
    ],
    [
      identificationTypeDNILabel,
      identificationTypeNIELabel,
      identificationTypePassportLabel,
    ]
  );

  const searchParams = useSearchParams();
  const getAgeFromPolicyStartForAMT =
    current?.uiElementControls?.getAgeFromPolicyStartForAMT;
  const filterUnsupportedTitlesForSpainQuote =
    current?.brandSpecificParams?.removeUnsupportedTitles;
  const editPageTraveller = searchParams.get(TravellerEdit);
  const editPageOrganizer = searchParams.get(OrganizerEdit);

  const isEditFlow =
    editPageTraveller === "true" || editPageOrganizer === "true";

  const isUnsupportedTitle = useMemo(() => {
    return filterUnsupportedTitlesForSpainQuote && isEditFlow;
  }, [filterUnsupportedTitlesForSpainQuote, isEditFlow]);

  // Check if identification fields should be shown (Spain only)
  const showIdentificationFields = useMemo(() => {
    return current?.brandSpecificParams?.showIdentificationFields === true;
  }, [current]);

  const question = useSelector((state: RootState) => state.quote);

  const isAMT =
    question.attributes.tripDetails?.policyType === ANNUAL_MULTI_TRIP;

  // Check if user is in Spain hub A/B test variant (hide identification fields)
  const personalDetailsHidden = useMemo(() => {
    return hidePersonalDetailsFields(question.attributes?.experimentDetails);
  }, [question.attributes?.experimentDetails]);

  const initialFocusState = useMemo(() => {
    const state: Record<string, boolean> = {};
    for (let i = 0; i < formData.length; i++) {
      state[`${i}_value1`] = false; // Title
      state[`${i}_value2`] = false; // First name
      state[`${i}_value3`] = false; // Last name
      state[`${i}_dateValue`] = false; // DOB
      if (current?.brandSpecificParams?.nationalityField) {
        state[`${i}_nationalityValue`] = false; // Nationality
      }
      if (showIdentificationFields) {
        state[`${i}_identificationType`] = false;
        state[`${i}_identificationNumber`] = false;
      }
    }
    return state;
  }, [
    formData.length,
    showIdentificationFields,
    current?.brandSpecificParams?.nationalityField,
  ]);

  const {
    getWarningStatus: getTrafficLightWarningStatus,
    handleBlur: clearFieldTouched,
    setFocusedInputs: markMultipleFieldsAsTouched,
  } = useInputFocus(initialFocusState);

  useEffect(() => {
    if (formNo && formNo > 0) {
      const formDataEdited = Array.from({ length: formNo }, (_, index) => ({
        ...formDataInitialValue,
        id: index + 1, // Assuming IDs start from 1
      }));
      setAgeErrors([]);
      setIsEditingDob(new Array(formNo).fill(false));
      setFormData(formDataEdited);
    }
  }, [formNo]);

  useEffect(() => {
    if (travellerDetailsEdit && travellerDetailsEdit.length > 0) {
      let dataToSet = travellerDetailsEdit;

      if (isUnsupportedTitle) {
        const drTitle =
          titleData.find((t) => t.key === "DR")?.displayName || "Dr";
        const revTitle =
          titleData.find((t) => t.key === "REV")?.displayName || "Rev";

        const hasUnsupportedTitles = travellerDetailsEdit.some(
          (form) =>
            form.value1 === drTitle ||
            form.value1 === revTitle ||
            form.value1 === "Dr" ||
            form.value1 === "Rev"
        );

        if (hasUnsupportedTitles) {
          dataToSet = travellerDetailsEdit.map((form) => {
            if (
              form.value1 === drTitle ||
              form.value1 === revTitle ||
              form.value1 === "Dr" ||
              form.value1 === "Rev"
            ) {
              return { ...form, value1: "" };
            }
            return form;
          });
        }
      }

      setFormData(dataToSet);
      setValidatedFormCount(dataToSet.length);

      const isComplete = isFormDataComplete(
        dataToSet,
        travellerType || Individual,
        organizer?.travelling || false,
        wizard,
        current,
        question.attributes?.experimentDetails
      );
      if (isComplete) {
        handleSubmitButton(dataToSet);
      } else {
        handleSubmitButton([]);
      }

      // validateFrom(travellerDetailsEdited);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travellerDetailsEdit, isUnsupportedTitle]);

  // Helper function to compute passport field visibility (derived state)
  const isShowPassportField = (form: FormDataType): boolean => {
    return (
      form.identificationType === "PASSPORT" && !!form.identificationNumber
    );
  };

  useEffect(() => {
    if (errorObjEdited) {
      const {
        ageErrors: ageEditdErrors,
        firstNameErrors: firstNameEditedErrors,
        lastNameErrors: lastNameEditedErrors,
      } = errorObjEdited;
      setAgeErrors(ageEditdErrors);
      setFirstNameErrors(firstNameEditedErrors);
      setLastNameErrors(lastNameEditedErrors);
      // validateFrom(travellerDetailsEdited);
    }
  }, [errorObjEdited]);

  useEffect(() => {
    function handleKeyDown(event: { key: string }) {
      if (event.key === "Tab") {
        setIsTabPressed(true);
      }
    }

    function handleKeyUp(event: { key: string }) {
      if (event.key === "Tab") {
        setIsTabPressed(false);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  const [formCount, setFormCount] = useState<number>(1);
  const [removeIndex, setRemoveIndex] = useState<number | null>(formCount); // allow null for no selection
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isAddTravelerBtnClick, setIsAddTravelerBtnClick] =
    useState<boolean>(false);

  useEffect(() => {
    if (travellerAdded && formData.length > 0 && !isLargeDesktop) {
      const lastElement = inputRefs.current[formData.length - 1];
      if (lastElement !== null) {
        lastElement.scrollIntoView({ behavior: "smooth", block: "start" });
        setTravellerAdded(false);
      }
    }
  }, [formData.length, travellerAdded, isLargeDesktop]);

  useEffect(() => {
    const hasAnyDateError = formData.some((form, index) =>
      getDateValidationStates(
        form,
        index,
        travellerType || Individual,
        organizer?.travelling || false,
        formData,
        (current?.brandSpecificParams?.ageLimitOrganizer as AgeLimitProps) || {}
      )
    );
    onTravellerDobValidationTrigger?.(hasAnyDateError);
  }, [
    formData,
    travellerType,
    organizer?.travelling,
    onTravellerDobValidationTrigger,
    current?.brandSpecificParams?.ageLimitOrganizer,
  ]);

  const markFieldsAboveAsTouched = (
    travellerIndex: number,
    currentField:
      | "value1"
      | "value2"
      | "value3"
      | "dateValue"
      | "nationalityValue"
  ) => {
    const fieldKey = `${travellerIndex}_${currentField}`;

    if (!current?.brandSpecificParams?.isTrafficLightEnabled) {
      return;
    }

    if (isSubmittingRef.current) {
      return;
    }

    if (isUpdatingFocusStateRef.current) {
      return;
    }

    const now = Date.now();
    const isSameField = lastMarkedFieldRef.current === fieldKey;
    const timeSinceLastCall = now - lastCascadeCallRef.current;

    if (isSameField && timeSinceLastCall < 100) {
      return;
    }

    lastCascadeCallRef.current = now;
    lastMarkedFieldRef.current = fieldKey;

    const fieldOrder = [
      "value1",
      "value2",
      "value3",
      ...(current?.brandSpecificParams?.nationalityField
        ? ["nationalityValue"]
        : []),
      "dateValue",
      "identificationType",
      "identificationNumber",
    ];
    const currentFieldIndex = fieldOrder.indexOf(currentField);

    const fieldsToMark: string[] = [];

    for (let i = 0; i <= currentFieldIndex; i++) {
      fieldsToMark.push(`${travellerIndex}_${fieldOrder[i]}`);
    }

    for (let i = 0; i < travellerIndex; i++) {
      fieldOrder.forEach((field) => {
        fieldsToMark.push(`${i}_${field}`);
      });
    }

    // Set flag to prevent re-entry
    isUpdatingFocusStateRef.current = true;

    markMultipleFieldsAsTouched(fieldsToMark);

    // Reset flag after state update completes (next tick)
    setTimeout(() => {
      isUpdatingFocusStateRef.current = false;
    }, 0);
  };

  const titleStatuses = useMemo(() => {
    if (!current?.brandSpecificParams?.isTrafficLightEnabled) {
      return formData.map(() => null);
    }

    return formData.map((form, index) => {
      return form.value1
        ? "success"
        : getTrafficLightWarningStatus(`${index}_value1`);
    });
  }, [
    formData,
    current?.brandSpecificParams?.isTrafficLightEnabled,
    getTrafficLightWarningStatus,
  ]);

  const firstNameStatuses = useMemo(() => {
    if (!current?.brandSpecificParams?.isTrafficLightEnabled) {
      return formData.map(() => null);
    }

    return formData.map((form, index) => {
      if (firstNameErrors[index]) return "error";
      if (form.value2) return "success";
      return getTrafficLightWarningStatus(`${index}_value2`);
    });
  }, [
    formData,
    firstNameErrors,
    current?.brandSpecificParams?.isTrafficLightEnabled,
    getTrafficLightWarningStatus,
  ]);

  const lastNameStatuses = useMemo(() => {
    if (!current?.brandSpecificParams?.isTrafficLightEnabled) {
      return formData.map(() => null);
    }

    return formData.map((form, index) => {
      if (lastNameErrors[index]) return "error";
      if (form.value3) return "success";
      return getTrafficLightWarningStatus(`${index}_value3`);
    });
  }, [
    formData,
    lastNameErrors,
    current?.brandSpecificParams?.isTrafficLightEnabled,
    getTrafficLightWarningStatus,
  ]);

  const dobStatuses = useMemo(() => {
    if (!current?.brandSpecificParams?.isTrafficLightEnabled) {
      return formData.map(() => null);
    }

    return formData.map((form, index) => {
      const age = form.value4 ? Number(form.value4) : null;
      const isFutureDate = age !== null && age < 0;
      const hasAgeError = ageErrors[index] && ageErrors[index].trim() !== "";

      const hasDateValidationError = getDateValidationStates(
        form,
        index,
        travellerType || Individual,
        organizer?.travelling || false,
        formData,
        (current?.brandSpecificParams?.ageLimitOrganizer as AgeLimitProps) || {}
      );

      if (
        isEditingDob[index] &&
        (hasAgeError || isFutureDate || hasDateValidationError)
      ) {
        return "warning";
      }

      if (hasAgeError || isFutureDate || hasDateValidationError) {
        return "error";
      }

      if (
        form.dateValue &&
        isValidDate(form.dateValue) &&
        form.value4 &&
        age !== null &&
        age >= 0
      ) {
        return "success";
      }

      return getTrafficLightWarningStatus(`${index}_dateValue`);
    });
  }, [
    formData,
    ageErrors,
    isEditingDob,
    current?.brandSpecificParams?.isTrafficLightEnabled,
    getTrafficLightWarningStatus,
    travellerType,
    organizer?.travelling,
    current?.brandSpecificParams?.ageLimitOrganizer,
  ]);

  const identificationTypeStatuses = useMemo(() => {
    if (
      !current?.brandSpecificParams?.isTrafficLightEnabled ||
      !showIdentificationFields
    ) {
      return formData.map(() => null);
    }

    return formData.map((form, index) => {
      const age = Number.parseInt(form.value4 || "0", 10) || 0;
      const hasAgeEntered = !!form.value4 && form.value4 !== "";

      if (hasAgeEntered && age < 18 && age >= 0) {
        return null;
      }

      return form.identificationType
        ? "success"
        : getTrafficLightWarningStatus(`${index}_identificationType`);
    });
  }, [
    formData,
    current?.brandSpecificParams?.isTrafficLightEnabled,
    showIdentificationFields,
    getTrafficLightWarningStatus,
  ]);

  const identificationNumberStatuses = useMemo(() => {
    if (
      !current?.brandSpecificParams?.isTrafficLightEnabled ||
      !showIdentificationFields
    ) {
      return formData.map(() => null);
    }

    return formData.map((form, index) => {
      const age = Number.parseInt(form.value4 || "0", 10) || 0;
      const hasAgeEntered = !!form.value4 && form.value4 !== "";

      if (hasAgeEntered && age < 18 && age >= 0) {
        return null;
      }

      if (!form.identificationType && form.identificationNumber) {
        return "error";
      }

      const validationError = validateIdentificationNumber(
        form.identificationType,
        form.identificationNumber || "",
        identificationNumberInvalidError || ""
      );
      if (validationError) return "error";

      if (form.identificationType && form.identificationNumber?.trim()) {
        return "success";
      }

      return getTrafficLightWarningStatus(`${index}_identificationNumber`);
    });
  }, [
    formData,
    current?.brandSpecificParams?.isTrafficLightEnabled,
    showIdentificationFields,
    getTrafficLightWarningStatus,
    identificationNumberInvalidError,
  ]);

  // Filtered identification types per traveller (moved from TravellerIdentificationFields)
  const filteredIdentificationTypes = useMemo(() => {
    return formData.map((form) => {
      return allIdentificationTypes.filter((type) => {
        // Show PASSPORT option only if there's saved passport data
        if (type.id === "PASSPORT") {
          return isShowPassportField(form);
        }
        // Show all other identification types
        return true;
      });
    });
  }, [formData, allIdentificationTypes]);

  // Identification number error messages per traveller (moved from TravellerIdentificationFields)
  const identificationNumberErrors = useMemo(() => {
    return formData.map((form, index) => {
      // Error case 1: User typing without selecting type
      if (!form.identificationType && form.identificationNumber) {
        return identificationTypeRequiredError || "";
      }

      // Error case 2: Invalid format validation
      const validationError = validateIdentificationNumber(
        form.identificationType,
        form.identificationNumber || "",
        identificationNumberInvalidError || ""
      );

      if (validationError) {
        return validationError;
      }

      // Warning case: Empty field with warning status (after interaction)
      if (
        identificationNumberStatuses[index] === "warning" &&
        !form.identificationNumber?.trim()
      ) {
        return identificationNumberWarning || "";
      }

      return "";
    });
  }, [
    formData,
    identificationTypeRequiredError,
    identificationNumberInvalidError,
    identificationNumberWarning,
    identificationNumberStatuses,
  ]);

  const nationalityStatuses = useMemo(() => {
    if (
      !current?.brandSpecificParams?.isTrafficLightEnabled ||
      !current?.brandSpecificParams?.nationalityField
    ) {
      return formData.map(() => null);
    }

    return formData.map((form, index) => {
      if (form.nationalityValue) {
        return "success";
      }
      return getTrafficLightWarningStatus(`${index}_nationalityValue`);
    });
  }, [
    formData,
    current?.brandSpecificParams?.isTrafficLightEnabled,
    current?.brandSpecificParams?.nationalityField,
    getTrafficLightWarningStatus,
  ]);

  const formInforWarningAndErrorCount = useMemo(() => {
    let totalCount = 0;

    formData.forEach((form, index) => {
      if (
        firstNameStatuses[index] === WARNING ||
        firstNameStatuses[index] === ERROR
      ) {
        totalCount++;
      }
      if (
        lastNameStatuses[index] === WARNING ||
        lastNameStatuses[index] === ERROR
      ) {
        totalCount++;
      }
      if (dobStatuses[index] === WARNING || dobStatuses[index] === ERROR) {
        totalCount++;
      }
      if (titleStatuses[index] === WARNING) {
        totalCount++;
      }
      if (nationalityStatuses[index] === WARNING && !personalDetailsHidden) {
        totalCount++;
      }
      if (
        (identificationNumberStatuses[index] === WARNING ||
          identificationNumberStatuses[index] === ERROR) &&
        !personalDetailsHidden
      ) {
        totalCount++;
      }
      if (
        identificationTypeStatuses[index] === WARNING &&
        !personalDetailsHidden
      ) {
        totalCount++;
      }
    });
    if (newMedicalFlowEnabled && hasMedicalConditions === undefined) {
      totalCount++;
    }
    return totalCount;
  }, [
    formData,
    newMedicalFlowEnabled,
    hasMedicalConditions,
    firstNameStatuses,
    lastNameStatuses,
    dobStatuses,
    titleStatuses,
    nationalityStatuses,
    personalDetailsHidden,
    identificationNumberStatuses,
    identificationTypeStatuses,
  ]);

  const errorDetails = useMemo(() => {
    if (!current?.brandSpecificParams?.isTrafficLightEnabled || !showErrors) {
      return { count: 0, firstError: "" };
    }

    const recordError = (
      errorKey: string,
      errorState: { count: number; firstError: string }
    ) => {
      errorState.count++;
      if (!errorState.firstError) errorState.firstError = errorKey;
    };

    const validateBasicFields = (
      form: FormDataType,
      index: number,
      errorState: { count: number; firstError: string }
    ) => {
      if (!form.value1) {
        recordError(`${index}_title`, errorState);
      }
      if (!form.value2 || firstNameErrors[index]) {
        recordError(`${index}_firstName`, errorState);
      }
      if (!form.value3 || lastNameErrors[index]) {
        recordError(`${index}_lastName`, errorState);
      }
    };

    const validateNationality = (
      form: FormDataType,
      index: number,
      errorState: { count: number; firstError: string }
    ) => {
      if (
        current?.brandSpecificParams?.nationalityField &&
        !form.nationalityValue
      ) {
        recordError(`${index}_nationality`, errorState);
      }
    };

    const validateDOB = (
      form: FormDataType,
      index: number,
      errorState: { count: number; firstError: string }
    ) => {
      const hasDobError = getDateValidationStates(
        form,
        index,
        travellerType || Individual,
        organizer?.travelling || false,
        formData,
        (current?.brandSpecificParams?.ageLimitOrganizer as AgeLimitProps) || {}
      );

      if (
        !form.dateValue ||
        !isValidDate(form.dateValue) ||
        !form.value4 ||
        ageErrors[index] ||
        hasDobError
      ) {
        recordError(`${index}_dob`, errorState);
      }
    };

    const validateEducationQuestion = (
      form: FormDataType,
      index: number,
      errorState: { count: number; firstError: string }
    ) => {
      const isFamilyType =
        travellerType === Family || travellerType === SingleParentFamily;
      if (!isFamilyType || !form.value4) return;

      const age = Number(form.value4);
      const needsEducationQuestion = age > 17 && age < 21;
      if (needsEducationQuestion && form.fullTimeEducation === undefined) {
        recordError(`${index}_fullTimeEducation`, errorState);
      }
    };

    const validateIdentificationFields = (
      form: FormDataType,
      index: number,
      errorState: { count: number; firstError: string }
    ) => {
      if (!showIdentificationFields || personalDetailsHidden) return;

      const age = Number.parseInt(form.value4 || "0", 10) || 0;
      const hasAgeEntered = !!form.value4 && form.value4 !== "";

      if (hasAgeEntered && age < 18 && age >= 0) {
        return;
      }

      if (!hasAgeEntered) {
        if (!form.identificationType) {
          recordError(`${index}_identificationType`, errorState);
        }
        if (!form.identificationNumber) {
          recordError(`${index}_identificationNumber`, errorState);
        }
        return;
      }

      const isRequired = age >= 18;
      if (!isRequired) return;

      if (!form.identificationType) {
        recordError(`${index}_identificationType`, errorState);
      }
      if (
        !form.identificationNumber ||
        validateIdentificationNumber(
          form.identificationType,
          form.identificationNumber || "",
          identificationNumberInvalidError || ""
        )
      ) {
        recordError(`${index}_identificationNumber`, errorState);
      }
    };

    const errorState = { count: 0, firstError: "" };
    const formsToValidate = formData.slice(0, validatedFormCount);

    formsToValidate.forEach((form, index) => {
      validateBasicFields(form, index, errorState);
      validateNationality(form, index, errorState);
      validateDOB(form, index, errorState);
      validateEducationQuestion(form, index, errorState);
      validateIdentificationFields(form, index, errorState);
    });

    if (
      newMedicalFlowEnabled &&
      hasMedicalConditions === undefined &&
      formData.length > 0
    ) {
      recordError("medical", errorState);
    }

    return errorState;
  }, [
    current?.brandSpecificParams?.isTrafficLightEnabled,
    current?.brandSpecificParams?.nationalityField,
    current?.brandSpecificParams?.ageLimitOrganizer,
    showErrors,
    formData,
    firstNameErrors,
    lastNameErrors,
    ageErrors,
    newMedicalFlowEnabled,
    hasMedicalConditions,
    showIdentificationFields,
    personalDetailsHidden,
    identificationNumberInvalidError,
    travellerType,
    organizer?.travelling,
    validatedFormCount,
  ]);

  const firstErrorRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!current?.brandSpecificParams?.isTrafficLightEnabled) {
      return;
    }

    if (validationTrigger !== undefined && validationTrigger > 0) {
      const newValidatedCount = formData.length;
      const fieldsToMark: string[] = [];
      const fieldOrder = [
        "value1",
        "value2",
        "value3",
        ...(current?.brandSpecificParams?.nationalityField
          ? ["nationalityValue"]
          : []),
        "dateValue",
        "identificationType",
        "identificationNumber",
      ];

      for (let i = 0; i < newValidatedCount; i++) {
        fieldOrder.forEach((field) => {
          fieldsToMark.push(`${i}_${field}`);
        });
      }

      markMultipleFieldsAsTouched(fieldsToMark);
      setValidatedFormCount(newValidatedCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationTrigger, current?.brandSpecificParams?.isTrafficLightEnabled]);

  useEffect(() => {
    if (
      !current?.brandSpecificParams?.isTrafficLightEnabled ||
      !showErrors ||
      formData.length <= validatedFormCount
    ) {
      return;
    }

    const unvalidatedForms = formData.slice(validatedFormCount);
    const hasAnyFieldFilled = unvalidatedForms.some(
      (form) =>
        form.value1 ||
        form.value2 ||
        form.value3 ||
        form.value4 ||
        form.nationalityValue ||
        form.dateValue ||
        form.identificationType ||
        form.identificationNumber
    );

    if (hasAnyFieldFilled) {
      setValidatedFormCount(formData.length);
    }
  }, [
    formData,
    validatedFormCount,
    showErrors,
    current?.brandSpecificParams?.isTrafficLightEnabled,
  ]);

  useEffect(() => {
    if (
      !current?.brandSpecificParams?.isTrafficLightEnabled ||
      !newMedicalFlowEnabled
    ) {
      return;
    }

    const prevValue = prevMedicalConditionsRef.current;
    const currentValue = hasMedicalConditions;

    if (prevValue === undefined && currentValue !== undefined) {
      const fieldsToMark: string[] = [];
      const fieldOrder = [
        "value1",
        "value2",
        "value3",
        ...(current?.brandSpecificParams?.nationalityField
          ? ["nationalityValue"]
          : []),
        "dateValue",
        "identificationType",
        "identificationNumber",
      ];

      for (let i = 0; i < formData.length; i++) {
        fieldOrder.forEach((field) => {
          fieldsToMark.push(`${i}_${field}`);
        });
      }

      markMultipleFieldsAsTouched(fieldsToMark);

      if (formData.length > validatedFormCount) {
        setValidatedFormCount(formData.length);
      }
    }

    prevMedicalConditionsRef.current = currentValue;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMedicalConditions]);

  const getAlertMessageIncompleteSection = useCallback(() => {
    if (errorDetails.count === 1) {
      return travellerAlertMessageIncompleteSectionSingle?.replace(
        "<ERROR_COUNT>",
        formInforWarningAndErrorCount.toString()
      );
    }
    return travellerAlertMessageIncompleteSection?.replace(
      "<ERROR_COUNT>",
      formInforWarningAndErrorCount.toString()
    );
  }, [
    errorDetails.count,
    formInforWarningAndErrorCount,
    travellerAlertMessageIncompleteSectionSingle,
    travellerAlertMessageIncompleteSection,
  ]);

  const getAlertMessageLinkSection = useCallback(() => {
    if (errorDetails.count === 1) {
      return travellerAlertMessageLinkSectionSingle;
    }
    return travellerAlertMessageLinkSection;
  }, [
    errorDetails.count,
    travellerAlertMessageLinkSectionSingle,
    travellerAlertMessageLinkSection,
  ]);

  useEffect(() => {
    if (showErrors && current?.brandSpecificParams?.isTrafficLightEnabled) {
      markMultipleFieldsAsTouched(Object.keys(initialFocusState));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showErrors, current?.brandSpecificParams?.isTrafficLightEnabled]);

  const calculateAgeFromDate = (dateValue: Date) => {
    const birthDate = dayjs(dateValue).startOf("day");
    const currentDate = dayjs().startOf("day");
    const standardAge = currentDate.diff(birthDate, "year");
    const isFutureDate = birthDate.isAfter(currentDate);
    const policyStartDate = question.attributes.tripDetails?.startDate
      ? dayjs(question.attributes.tripDetails.startDate).startOf("day")
      : null;
    const AMTAge = policyStartDate?.diff(birthDate, "year");

    const age =
      getAgeFromPolicyStartForAMT && isAMT
        ? AMTAge ?? standardAge
        : standardAge;

    if (isFutureDate) {
      return age === 0 ? -0.5 : age;
    }
    return age;
  };

  const shouldClearEducationQuestion = (ageValue: number) => {
    return !ageValue || Number(ageValue) < 17 || Number(ageValue) > 21;
  };

  const processDateValueChange = (
    newFormData: FormDataType[],
    index: number
  ) => {
    if (!isValidDate(newFormData[index].dateValue as Date)) {
      newFormData[index] = { ...newFormData[index], value4: "" };
      return;
    }

    const ageValue = calculateAgeFromDate(newFormData[index].dateValue as Date);
    newFormData[index] = {
      ...newFormData[index],
      value4: ageValue.toString(),
    };

    const isFamilyType =
      travellerType === Family || travellerType === SingleParentFamily;
    if (isFamilyType && shouldClearEducationQuestion(ageValue)) {
      newFormData[index] = {
        ...newFormData[index],
        fullTimeEducation: undefined,
      };
    }
  };

  const clearEducationIfNeeded = (
    newFormData: FormDataType[],
    index: number,
    value: string | boolean
  ) => {
    const isFamilyType =
      travellerType === Family || travellerType === SingleParentFamily;
    if (!isFamilyType) return;

    if (!value || (value && Number(value) < 17) || Number(value) > 21) {
      newFormData[index] = {
        ...newFormData[index],
        fullTimeEducation: undefined,
      };
    }
  };

  const validateFieldByType = (
    fieldName: string,
    newFormData: FormDataType[],
    updatedForm: FormDataType,
    index: number,
    value: string | boolean
  ) => {
    if (fieldName === "value2") {
      validateFirstName(updatedForm, index);
      return;
    }

    if (fieldName === "value3") {
      validateLastName(updatedForm, index);
      return;
    }

    if (fieldName === "dateValue") {
      if (travellerType === Group) {
        validateGroupData(newFormData, updatedForm);
      } else if (travellerType === Family) {
        validateFamilyData(newFormData[index], index);
      } else if (travellerType === SingleParentFamily) {
        validateSingleFamilyData(newFormData[index], index);
      } else {
        validateAge(newFormData[index], index);
      }
      return;
    }

    if (fieldName === "value4") {
      clearEducationIfNeeded(newFormData, index, value);

      if (travellerType === Family) {
        validateFamilyData(updatedForm, index);
      } else if (travellerType === SingleParentFamily) {
        validateSingleFamilyData(updatedForm, index);
      } else if (travellerType === Group) {
        validateGroupData(newFormData, updatedForm);
      } else {
        validateAge(updatedForm, index);
      }
      return;
    }

    if (fieldName === "fullTimeEducation") {
      if (travellerType === Family) {
        validateFamilyData(updatedForm, index);
      } else if (travellerType === SingleParentFamily) {
        validateSingleFamilyData(updatedForm, index);
      }
    }
  };

  const processNationalityChange = (
    newFormData: FormDataType[],
    index: number,
    value: string | boolean
  ): boolean => {
    if (!nationalityData || nationalityData.length === 0) {
      return false;
    }
    const selectedNationality = nationalityData.find((n) => n.name === value)
      ?.id;
    newFormData[index].nationalityValue = selectedNationality || undefined;
    return true;
  };

  const submitFormData = (newFormData: FormDataType[]) => {
    const isComplete = isFormDataComplete(
      newFormData,
      travellerType || Individual,
      organizer?.travelling || false,
      wizard,
      current,
      question.attributes?.experimentDetails
    );

    if (isComplete) {
      isSubmittingRef.current = true;
      handleSubmitButton(newFormData);
    } else {
      isSubmittingRef.current = false;
      handleSubmitButton([]);
    }
  };

  const handleInputChange = useCallback(
    (index: number, fieldName: string, value: string | boolean) => {
      // Pure computation - compute next state without side effects
      const newFormData = [...formData];
      const updatedForm = { ...newFormData[index], [fieldName]: value };
      newFormData[index] = updatedForm;

      if (fieldName === "dateValue" || newFormData[index].dateValue) {
        processDateValueChange(newFormData, index);
      }

      newFormData[index] = { ...newFormData[index], [fieldName]: value };

      if (fieldName === "nationalityValue") {
        const success = processNationalityChange(newFormData, index, value);
        if (!success) {
          return; // Early return - bail out without updating state
        }
      }

      // Apply the state update
      setFormData(newFormData);

      // Side effects after state update - validators call setState, submitFormData
      // calls the parent's accessFormData which also calls setState
      validateFieldByType(fieldName, newFormData, updatedForm, index, value);
      submitFormData(newFormData);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData, travellerType, nationalityData, organizer?.travelling]
  );

  useGtmPopup({
    tripType: QuestionSeven,
    isPopupOpen: isModalOpen,
    popupName: popupMessage || "Remove traveller",
  });

  const validateFirstName = (form: FormDataType, index: number) => {
    const errors: string[] = validateName(
      form.value2,
      inputOneErrorNumericvalues || "",
      inputOneErrorSpecialCharacters || "",
      inputOneErrorMaximumNumber || "",
      wizard
    );

    const newFirstNameErrors = [...firstNameErrors];
    newFirstNameErrors[index] = errors.join(". ");
    setFirstNameErrors(newFirstNameErrors);

    if (!form.value1) {
      const newTitleErrors = [...titleErrors];
      newTitleErrors[index] = "title Error";

      setTitleErrors(newTitleErrors);
    }
  };
  const handleFullTimeEducation = (index: number, answer: number) => {
    if (answer) {
      handleInputChange(
        index,
        "fullTimeEducation",
        findAnswerQThreeById(answer)?.value || false
      );
    }
  };

  const validateLastName = (form: FormDataType, index: number) => {
    const errors: string[] = validateName(
      form.value3,
      inputTwoErrorNumericvalues || "",
      inputTwoErrorSpecialCharacters || "",
      inputTwoErrorMaximumNumber || "",
      wizard
    );

    const newLastNameErrors = [...lastNameErrors];
    newLastNameErrors[index] = errors.join(". ");
    setLastNameErrors(newLastNameErrors);
  };

  const validateAge = (form: FormDataType, index: number) => {
    const errors: string[] = validateDate(
      form,
      travellerType || "",
      dobMinAge || "",
      coupleMinAge || "",
      dobMaxAge || ""
    );
    const newAgeErrors = [...ageErrors];
    newAgeErrors[index] = errors.join(". ");
    setAgeErrors(newAgeErrors);
  };
  const validateFamilyData = (form: FormDataType, index: number) => {
    const errors: string[] = isFamilyValidate(
      index,
      form,
      organizer?.travelling || false,
      ageParentFamily,
      ageFamilyFullTimeEducation,
      (current?.brandSpecificParams?.ageLimitOrganizer as AgeLimitProps) ?? {}
    );
    const errors2: string[] = validateDate(
      form,
      travellerType || "",
      dobMinAge || "",
      coupleMinAge || "",
      dobMaxAge ?? "",
      (current?.brandSpecificParams?.ageLimitOrganizer as AgeLimitProps) ?? {}
    );

    const newAgeErrors = [...ageErrors];
    if (errors2 && errors2.length > 0) {
      newAgeErrors[index] = errors2.join(". ");
    } else if (errors && errors.length > 0) {
      newAgeErrors[index] = errors.join(". ");
    } else {
      newAgeErrors[index] = "";
    }

    setAgeErrors(newAgeErrors);
  };

  const validateSingleFamilyData = (form: FormDataType, index: number) => {
    const errors: string[] = isSingleFamilyValidate(
      index,
      form,
      organizer?.travelling || false,
      ageSingleParentFamily,
      ageSingleParentFullTimeEducation,
      (current?.brandSpecificParams?.ageLimitOrganizer as AgeLimitProps) ?? {}
    );
    const errors2: string[] = validateDate(
      form,
      travellerType || "",
      dobMinAge || "",
      coupleMinAge || "",
      dobMaxAge ?? "",
      (current?.brandSpecificParams?.ageLimitOrganizer as AgeLimitProps) ?? {}
    );

    const newAgeErrors = [...ageErrors];
    if (errors2 && errors2.length > 0) {
      newAgeErrors[index] = errors2.join(". ");
    } else if (errors && errors.length > 0) {
      newAgeErrors[index] = errors.join(". ");
    } else {
      newAgeErrors[index] = "";
    }
    setAgeErrors(newAgeErrors);
  };

  const validateGroupData = (
    newFormData: FormDataType[],
    form: FormDataType
  ) => {
    const errors: string[] = getGroupValidate(
      newFormData,
      form,
      ageGroupUnderTwo || ""
    );

    const errors2: string[] = validateDateGroup(
      form,
      newFormData,
      dobMinAge || "",
      dobMaxAge || ""
    );
    const newAgeErrors = [...ageErrors];

    if (errors2 && errors2.length > 0) {
      errors2.forEach((error, indexCheck) => {
        if (error) {
          newAgeErrors[indexCheck] = error;
        } else {
          newAgeErrors[indexCheck] = "";
        }
      });
      setAgeErrors(newAgeErrors);
    } else if (errors && errors.length > 0 && !organizer?.travelling) {
      errors.forEach((error, indexCheck) => {
        if (error) {
          newAgeErrors[indexCheck] = error;
        } else {
          newAgeErrors[indexCheck] = "";
        }
      });
      setAgeErrors(newAgeErrors);
    }

    if (errors2.length === 0 && errors.length === 0) {
      setAgeErrors([]);
    }
  };
  const handleAddForm = () => {
    if (current?.brandSpecificParams?.isTrafficLightEnabled) {
      const fieldsToMark: string[] = [];
      const fieldOrder = [
        "value1",
        "value2",
        "value3",
        ...(current?.brandSpecificParams?.nationalityField
          ? ["nationalityValue"]
          : []),
        "dateValue",
        "identificationType",
        "identificationNumber",
      ];

      for (let i = 0; i < formData.length; i++) {
        fieldOrder.forEach((field) => {
          fieldsToMark.push(`${i}_${field}`);
        });
      }

      markMultipleFieldsAsTouched(fieldsToMark);

      const newTitleErrors = [...titleErrors];
      formData.forEach((form, index) => {
        if (!form.value1) {
          newTitleErrors[index] = "title Error";
        }
        if (form.value2) {
          validateFirstName(form, index);
        }
        if (form.value3) {
          validateLastName(form, index);
        }
        if (form.dateValue || form.value4) {
          if (travellerType === Group) {
            validateGroupData(formData, form);
          } else if (travellerType === Family) {
            validateFamilyData(form, index);
          } else if (travellerType === SingleParentFamily) {
            validateSingleFamilyData(form, index);
          } else {
            validateAge(form, index);
          }
        }
      });
      setTitleErrors(newTitleErrors);
    }

    setIsAddTravelerBtnClick(true);
    setTimeout(() => {
      setIsAddTravelerBtnClick(false);
    }, 500);
    setFormCount(formCount + 1);
    setFormData([
      ...formData,
      { ...formDataInitialValue, ...{ id: formData.length + 1 } },
    ]);
    setFirstNameErrors([...firstNameErrors, ""]);
    setLastNameErrors([...lastNameErrors, ""]);
    setTravellerAdded(true);
  };

  const handleRemoveForm = () => {
    if (removeIndex === null) return;
    const newFormData = [...formData];
    newFormData.splice(removeIndex, 1);
    const newFirstNameErrors = [...firstNameErrors];
    newFirstNameErrors.splice(removeIndex, 1);
    const newLastNameErrors = [...lastNameErrors];
    newLastNameErrors.splice(removeIndex, 1);
    setFormData(newFormData);
    setFirstNameErrors(newFirstNameErrors);
    setLastNameErrors(newLastNameErrors);
    setFormCount(formCount - 1);
    const isComplete = isFormDataComplete(
      newFormData,
      travellerType || Individual,
      organizer?.travelling || false,
      wizard,
      current,
      question.attributes?.experimentDetails
    );
    if (isComplete) {
      handleSubmitButton(newFormData);
    } else {
      handleSubmitButton([]);
    }
    setTravellerAdded(true);
  };

  const firstNameRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const lastNameRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const dobRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const titleRefs = useRef<(HTMLDivElement | null)[]>([]);
  const nationalityRefs = useRef<(HTMLDivElement | null)[]>([]);
  const identificationNumberRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const fullTimeEducationRefs = useRef<(HTMLDivElement | null)[]>([]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // handleSubmitButton('newFormDatas');
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleAgeButtonPressed = (index: number) => {
    const newFormData = [...formData];
    newFormData[index].value4 = "";
    newFormData[index].dateValue = undefined;
    newFormData[index].isDateCalenter = !newFormData[index].isDateCalenter;
    setFormData(newFormData);
    const updatedErrors = [...ageErrors];
    updatedErrors[index] = "";
    setAgeErrors(updatedErrors);
    // setAgeErrors([]);
    handleSubmitButton([]);
  };

  const shouldScrollForCouple = (index: number) => {
    return index === 1 || (index === 0 && organizer?.travelling);
  };

  const shouldScrollForFamily = (index: number) => {
    return (
      (!organizer?.travelling && index === 8) ||
      (organizer?.travelling && index === 7)
    );
  };

  const shouldScrollForGroup = (index: number) => {
    return (
      (organizer?.travelling && index === 7) ||
      (!organizer?.travelling && index === 8)
    );
  };

  const shouldScrollToMedicalQuestion = (index: number, age: number) => {
    const isFamilyType =
      travellerType === SingleParentFamily || travellerType === Family;

    if (isFamilyType && index === 8 && age > 17) {
      setScrollMedicalQuestion(false);
      return;
    }

    if (travellerType === Individual) {
      setScrollMedicalQuestion(true);
      return;
    }

    if (travellerType === Couple && shouldScrollForCouple(index)) {
      setScrollMedicalQuestion(true);
      return;
    }

    if (isFamilyType && shouldScrollForFamily(index)) {
      setScrollMedicalQuestion(true);
      return;
    }

    if (travellerType === Group && shouldScrollForGroup(index)) {
      setScrollMedicalQuestion(true);
    }
  };

  const handleFocus = (index: number) => {
    setFocusedIndex(index);
    const focusableIndices = [0, 1, 2, 3, 4, 5, 6, 7, 8];
    if (focusedIndex === null || focusableIndices.includes(index)) {
      setIsTitleFocused(true);
    }
  };

  const getFormWrapperClassName = (index: number) => {
    const isFirst = index === 0;
    const isLast = index === formData.length - 1;

    const firstClass = isFirst
      ? "border-b pt-0 pb-spacing-l"
      : "border-b pt-spacing-l pb-spacing-l ";
    const lastClass = isLast ? "border-b-0 py-0 mb-[-24px]" : "";

    return `${Styles.form_wrapper} ${firstClass} ${lastClass}`;
  };

  const shouldShowRemoveButtonFamily = (index: number) => {
    if (!multipleForm || travellerType !== Family) return false;
    return (
      (index > 2 && !organizer?.travelling) ||
      (index > 1 && organizer?.travelling)
    );
  };

  const shouldShowRemoveButtonOther = (index: number) => {
    if (!multipleForm) return false;
    const isGroupOrSingleParent =
      travellerType === Group || travellerType === SingleParentFamily;
    if (!isGroupOrSingleParent) return false;

    return (
      (index > 1 && !organizer?.travelling) ||
      (index > 0 && organizer?.travelling)
    );
  };

  const shouldShowEducationQuestion = (form: FormDataType) => {
    const isFamilyType =
      travellerType === Family || travellerType === SingleParentFamily;
    if (!isFamilyType || !form.value4) return false;

    const age = Number(form.value4);
    return age > 17 && age < 21;
  };

  const getTitleDropdownClassName = (index: number) => {
    const focusedClass =
      isTitleFocused && focusedIndex === index ? Styles.dropdown_focused : "";
    const pointerEvents = isAddTravelerBtnClick ? "none" : "auto";

    return `${Styles.country_wrapper} ${focusedClass} pointer-events-${pointerEvents} flex flex-row items-start max-w-[106px] w-full h-[56px]`;
  };

  const getDropdownButtonClassName = (status: string | null) => {
    const warningClass = status === "warning" ? Styles.warningMsgClass : "";
    const successClass = status === "success" ? Styles.successMsgClass : "";

    return `${Styles.country_select} ${Styles.dropdown_btn} ${warningClass} ${successClass} lg:pl-[24px] py-spacing-m px-spacing-m flex space-between max-w-[106px] w-full border border-interactive-secondary-default rounded-radius-lg max-h-spacing-xxxl`;
  };

  const getDobWrapperClassName = (status: string | null) => {
    const warningClass = status === "warning" ? Styles.warningMsgClass : "";
    const successClass = status === "success" ? Styles.successMsgClass : "";
    const errorClass = status === "error" ? Styles.errorMsgClass : "";
    const maxWidthClass = current?.brandSpecificParams?.isTrafficLightEnabled
      ? "!max-w-[170px] md:!max-w-[200px] !flex-shrink-0"
      : "";

    return `${Styles.dobWrapper} ${warningClass} ${successClass} ${errorClass} ${maxWidthClass}`;
  };

  const getNationalityDropdownClassName = (status: string | null) => {
    const warningClass = status === "warning" ? Styles.warningMsgClass : "";
    const successClass = status === "success" ? Styles.successMsgClass : "";

    return `${NationalityStyles.droplistBox} ${warningClass} ${successClass} border-interactive-secondary-default rounded-radius-lg max-h-spacing-xxxl`;
  };

  const handleTitleSelectionChange = (index: number, value: string) => {
    handleInputChange(index, "value1", value);

    if (isdesktop) {
      setActiveHoverd(false);
      setInputDisable(true);
      setTimeout(() => setInputDisable(false), 1);
      setTimeout(() => setActiveHoverd(true), 1200);
    } else {
      setActiveHoverd(false);
      setInputDisable(true);
      setTimeout(() => setInputDisable(false), 1);
    }
  };

  const handleNationalitySelectionChange = (
    index: number,
    value: string,
    userTriggered: boolean,
    previousValue: string | undefined
  ) => {
    handleInputChange(index, "nationalityValue", value);

    if (userTriggered && previousValue !== value) {
      setTimeout(() => {
        lastNameRefs.current[index]?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }

    previousNationalityRef.current[index] = value;
    nationalityUserTriggeredRef.current[index] = false;

    if (isdesktop) {
      setActiveHoverd(false);
      setInputDisable(true);
      setTimeout(() => setInputDisable(false), 1);
      setTimeout(() => setActiveHoverd(true), 1200);
    } else {
      setActiveHoverd(false);
      setInputDisable(true);
      setTimeout(() => setInputDisable(false), 1);
    }
  };

  const scrollToDobRef = (index: number) => {
    setTimeout(() => {
      const ref = current?.brandSpecificParams?.nationalityField
        ? nationalityRefs
        : lastNameRefs;
      ref.current[index]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handleDobDateChange = (index: number, date: Date | unknown) => {
    if (!date) {
      handleInputChange(index, "dateValue", "");
      return;
    }

    if (date === "Invalid Date") {
      handleInputChange(index, "dateValue", "Invalidate Date of Birth");
      return;
    }

    if (isValidDate(date as Date)) {
      const age = calculateTravellerAge(date as string);
      shouldScrollToMedicalQuestion(index, age);
      if (formData.length) {
        handleInputChange(index, "dateValue", date?.toString() || "");
      }
    }
  };

  const handleIdentificationFieldsFocus = useCallback(
    (fields: string[], travellerIndex: number) => {
      const fieldOrder = [
        "value1",
        "value2",
        "value3",
        ...(current?.brandSpecificParams?.nationalityField
          ? ["nationalityValue"]
          : []),
        "dateValue",
        "identificationType",
        "identificationNumber",
      ];

      const fieldsToMark: string[] = [];

      const currentField = fields[fields.length - 1];
      const currentFieldIndex = fieldOrder.indexOf(currentField);

      for (let i = 0; i <= currentFieldIndex; i++) {
        fieldsToMark.push(`${travellerIndex}_${fieldOrder[i]}`);
      }

      for (let i = 0; i < travellerIndex; i++) {
        fieldOrder.forEach((f) => {
          fieldsToMark.push(`${i}_${f}`);
        });
      }

      markMultipleFieldsAsTouched(fieldsToMark);
    },
    [
      current?.brandSpecificParams?.nationalityField,
      markMultipleFieldsAsTouched,
    ]
  );

  // Optimized handler for identification dropdown focus state
  const handleIdentificationDropdownFocus = useCallback(
    (index: number, isFocused: boolean) => {
      setIsIdentificationDropdownFocused((prev) => {
        const newStates = [...prev];
        newStates[index] = isFocused;
        return newStates;
      });
    },
    []
  );

  // Memoized arrays of handlers per traveller index (prevents infinite re-renders)
  // Only recreate when number of travellers changes, not when their data changes
  const identificationTypeChangeHandlers = useMemo(
    () =>
      formData.map(
        (_, index) => (value: string) =>
          handleInputChange(index, "identificationType", value)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData.length, handleInputChange]
  );

  const identificationTypeBlurHandlers = useMemo(
    () =>
      formData.map(
        (_, index) => () =>
          handleIdentificationFieldsFocus(["identificationType"], index)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData.length, handleIdentificationFieldsFocus]
  );

  const identificationNumberChangeHandlers = useMemo(
    () =>
      formData.map(
        (_, index) => (value: string) =>
          handleInputChange(index, "identificationNumber", value)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData.length, handleInputChange]
  );

  const identificationNumberBlurHandlers = useMemo(
    () =>
      formData.map(
        (_, index) => () =>
          handleIdentificationFieldsFocus(["identificationNumber"], index)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData.length, handleIdentificationFieldsFocus]
  );

  const identificationTypeFocusHandlers = useMemo(
    () =>
      formData.map((_, index) => () => {
        if (isTabPressed) {
          handleIdentificationDropdownFocus(index, true);
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData.length, isTabPressed, handleIdentificationDropdownFocus]
  );

  const identificationTypeDropdownBlurHandlers = useMemo(
    () =>
      formData.map(
        (_, index) => () => handleIdentificationDropdownFocus(index, false)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData.length, handleIdentificationDropdownFocus]
  );

  return (
    <div
      data-testid="traveller-form-id"
      className=" flex flex-col gap-x-2 mt-spacing-l"
    >
      {/* <h2
        data-testid="traveller-form-subheading-id"
        className=" font-body-font text-2xs leading-[24px] font-semibold 
      md:mb-spacing-s mb-spacing-xs text-text-body md:pl-0 pl-spacing-m"
      >
        {subText}
      </h2>  */}
      <form
        data-testid="traveller-form"
        onSubmit={handleSubmit}
        className={`${Styles.trvellers_form} md:bg-surface-neutral-option-1 bg-surface-neutral-option-2`}
      >
        <div className="field-block md:!pb-0">
          {formData?.map((form, index) => (
            <div
              key={form.id}
              id={`form-scrollableInput-${index + 1}`}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              className={getFormWrapperClassName(index)}
            >
              <div
                data-testid="traveller-form-family-label"
                className={`${getFontClasses(
                  brand,
                  isdesktop
                )} text-text-title font-header-font text-[20px] mb-spacing-s`}
              >
                {getTravellerRole(
                  organizer,
                  index,
                  travellerType,
                  t("parent", "Parent"),
                  t("child", "Child")
                )}
              </div>
              <InputValidationWrapper
                className="flex flex-row mb-spacing-s"
                status={titleStatuses[index]}
              >
                <div
                  data-testid={`traveller-form-dropdown-${index}`}
                  className={getTitleDropdownClassName(index)}
                  onFocus={() => {
                    if (isTabPressed) {
                      handleFocus(index);
                    }
                  }}
                  onBlur={() => {
                    setIsTitleFocused(false);
                  }}
                  ref={(el) => {
                    titleRefs.current[index] = el;
                    if (errorDetails.firstError === `${index}_title`) {
                      firstErrorRef.current = el;
                    }
                  }}
                >
                  <Dropdown
                    id="traveler-form-dropdown"
                    alertState={titleErrors[index] ? "error" : undefined}
                    defaultValue={form.value1}
                    placeholderText={personalStatusPlaceholder}
                    strokeColor={getStrokeColor(brand)}
                    openDelayMs={200}
                    enableOpenDelay
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onSelectionChange={(e: any) => {
                      handleTitleSelectionChange(index, e);
                    }}
                    onDropdownBlur={() => {
                      if (
                        getTrafficLightWarningStatus(`${index}_value1`) === null
                      ) {
                        markFieldsAboveAsTouched(index, "value1");
                      }
                    }}
                    className={getDropdownButtonClassName(titleStatuses[index])}
                    listBoxStyle={{
                      outline: "none",
                      margin: "0px 0px 0px 0px",
                      minWidth: "unset !important",
                      boxShadow: "0px 0px 12px 0px rgba(15, 23, 42, 0.11)",
                    }}
                    brand={brand}
                    listClassName={` ${Styles.listStyle} rounded-radius-s`}
                    unorderedListClassName="lg:w-[414px] md:w-[400px] w-[calc(100vw-80px)] !md:ml-0 !ml-[-1px] !mt-[-4px] !border-border-subdued !rounded-radius-lg 
               !px-spacing-m !py-spacing-xs"
                  >
                    {titleData &&
                      titleData.map((title) => (
                        <DropdownItem value={title.displayName} key={title.id}>
                          <span
                            className=" w-full leading-[24px] font-proximanova text-2xs font-semibold p-spacing-xs 
                     country-name "
                          >
                            {title.displayName}
                          </span>
                        </DropdownItem>
                      ))}
                  </Dropdown>
                </div>
              </InputValidationWrapper>
              <InputValidationWrapper
                className="flex flex-row mb-spacing-s"
                status={firstNameStatuses[index]}
              >
                <button
                  type="button"
                  tabIndex={-1}
                  className={`${Styles.input_wrapper} flex flex-row items-start w-full focus-visible:outline-none`}
                  data-testid={`fist-name-wrapper-${index}`}
                  ref={(el) => {
                    firstNameRefs.current[index] = el;
                    if (errorDetails.firstError === `${index}_firstName`) {
                      firstErrorRef.current = el;
                    }
                  }}
                  onFocus={() => {
                    clearFieldTouched(`${index}_value2`);

                    if (index === 0) {
                      clickedFormTitle?.();
                    } else {
                      setTimeout(() => {
                        titleRefs.current[index]?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }, 100);
                    }
                  }}
                  onBlur={() => {
                    markFieldsAboveAsTouched(index, "value2");
                  }}
                >
                  <TextFieldSts
                    isDisabled={inputDisable}
                    activeHoverd={activeHoverd}
                    label={inputOnePlaceholder}
                    id={`traveller-first-name-${index + 1}`}
                    aria-label={`first-name-${index + 1}`}
                    width="100%"
                    name="value2"
                    maxLength={51}
                    state={
                      firstNameStatuses[index] ??
                      (firstNameErrors[index] ? "error" : undefined)
                    }
                    message={firstNameErrors[index]}
                    value={form.value2}
                    onChange={(value) =>
                      handleInputChange(index, "value2", value)
                    }
                    brand={brand}
                    msgCustomStyles={{ width: "auto" }}
                  />
                </button>
              </InputValidationWrapper>
              <InputValidationWrapper
                className="flex flex-row mb-spacing-s"
                status={lastNameStatuses[index]}
              >
                <button
                  type="button"
                  tabIndex={-1}
                  className={`${Styles.input_wrapper} flex flex-row items-start w-full focus-visible:outline-none`}
                  data-testid={`lastname-wrapper-${index}`}
                  ref={(el) => {
                    lastNameRefs.current[index] = el;
                    if (errorDetails.firstError === `${index}_lastName`) {
                      firstErrorRef.current = el;
                    }
                  }}
                  onFocus={() => {
                    clearFieldTouched(`${index}_value3`);

                    setTimeout(() => {
                      firstNameRefs.current[index]?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }, 300);
                  }}
                  onBlur={() => {
                    markFieldsAboveAsTouched(index, "value3");
                  }}
                >
                  <TextFieldSts
                    isDisabled={inputDisable}
                    activeHoverd={activeHoverd}
                    label={inputTwoPlaceholder}
                    id={`traveller-last-name-${form.id}-${index + 1}`}
                    aria-label={`last-name-${form.id}-${index + 1}`}
                    width="100%"
                    name="value3"
                    maxLength={51}
                    state={
                      lastNameStatuses[index] ??
                      (lastNameErrors[index] ? "error" : undefined)
                    }
                    message={lastNameErrors[index]}
                    value={form.value3}
                    onChange={(value) =>
                      handleInputChange(index, "value3", value)
                    }
                    brand={brand}
                    msgCustomStyles={{ width: "auto" }}
                  />
                </button>
              </InputValidationWrapper>

              {/* NATIONALITY */}
              {current?.brandSpecificParams?.nationalityField && (
                <InputValidationWrapper
                  className="flex flex-row mb-spacing-s"
                  status={nationalityStatuses[index]}
                >
                  <div
                    data-testid="nationality-wrapper"
                    className={` ${NationalityStyles.country_wrapper} ${
                      NationalityStyles.title_dropdown
                    } ${
                      isNationalityFocused && NationalityStyles.dropdown_focused
                    } flex flex-row items-start w-full h-[56px]`}
                    onFocus={() => {
                      if (isTabPressed) {
                        setIsNationalityFocused(true);
                      }
                    }}
                    onBlur={() => {
                      setIsNationalityFocused(false);
                    }}
                    ref={(el) => {
                      nationalityRefs.current[index] = el;
                      if (errorDetails.firstError === `${index}_nationality`) {
                        firstErrorRef.current = el;
                      }
                    }}
                  >
                    <Dropdown
                      id="nationality-dropdown"
                      defaultValue={
                        nationalityData.find(
                          (n) => n.id === formData[index].nationalityValue
                        )?.name ?? undefined
                      }
                      placeholderTextAndLabel={
                        nationalityPlaceholder ?? "Nationality"
                      }
                      strokeColor={getStrokeColor(brand)}
                      onOpenChange={(open: boolean) => {
                        if (open) {
                          nationalityUserTriggeredRef.current[index] = true;
                        }
                      }}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      onSelectionChange={(e: any) => {
                        const userTriggered =
                          nationalityUserTriggeredRef.current[index] === true;
                        const prev = previousNationalityRef.current[index];
                        handleNationalitySelectionChange(
                          index,
                          e,
                          userTriggered,
                          prev
                        );
                      }}
                      onDropdownBlur={() => {
                        markFieldsAboveAsTouched(index, "nationalityValue");
                      }}
                      className={getNationalityDropdownClassName(
                        nationalityStatuses[index]
                      )}
                      placeholderLeftBeforeSelect={0}
                      placeholderLeftAfterSelect={-24}
                      listBoxStyle={{
                        outline: "none",
                        margin: "0px 0px 0px 0px",
                        minWidth: "unset !important",
                        maxHeight: "400px",
                        boxShadow: "0px 0px 12px 0px rgba(15, 23, 42, 0.11)",
                      }}
                      listClassName={`${NationalityStyles.listStyle} mt-[10px] rounded-radius-s`}
                      unorderedListClassName="w-full !mt-[3px] !md:ml-0 !ml-[-1px] !border-border-subdued !rounded-radius-lg !px-spacing-m !py-spacing-xs"
                    >
                      {nationalityData &&
                        nationalityData.map((nationality) => (
                          <DropdownItem
                            value={nationality.name}
                            key={nationality.id}
                          >
                            <span
                              className=" w-full leading-[24px] font-proximanova text-2xs font-semibold p-spacing-xs
                     country-name "
                            >
                              {nationality.name}
                            </span>
                          </DropdownItem>
                        ))}
                    </Dropdown>
                  </div>
                </InputValidationWrapper>
              )}

              {form.isDateCalenter ? (
                <div className="flex flex-col w-full mb-spacing-s">
                  <button
                    type="button"
                    tabIndex={-1}
                    className={`${Styles.date_picker_wrapper} flex flex-col relative w-full focus-visible:outline-none`}
                    ref={(el) => {
                      dobRefs.current[index] = el;
                      if (errorDetails.firstError === `${index}_dob`) {
                        firstErrorRef.current = el;
                      }
                    }}
                    data-testid={`dob-wrapper-${index}`}
                    onClick={() => scrollToDobRef(index)}
                  >
                    <InputValidationWrapper
                      className={`${Styles.date_picker_wrapper} flex flex-row relative sm:!justify-between md:!justify-normal`}
                      status={dobStatuses[index]}
                      isDateInput={true}
                    >
                      <DateInput
                        dayId={`traveller-form-day-${index}`}
                        monthId={`traveller-form-month-${index}`}
                        yearId={`traveller-form-year-${index}`}
                        isDOBScrollable={false}
                        scrollableInputId={`scrollableInput-${index}`}
                        defaultDate={
                          form.dateValue && isValidDate(form.dateValue)
                            ? new Date(form.dateValue)
                            : undefined
                        }
                        hintLabel={dobPlaceholder}
                        customizedMessage=""
                        isError={getDateValidationStates(
                          form,
                          index,
                          travellerType || Individual,
                          organizer?.travelling || false,
                          formData,
                          (current?.brandSpecificParams
                            ?.ageLimitOrganizer as AgeLimitProps) || {}
                        )}
                        dobWrapperClass={getDobWrapperClassName(
                          dobStatuses[index]
                        )}
                        dobErrorMsgClass={
                          current?.brandSpecificParams?.isTrafficLightEnabled
                            ? `${Styles.errorOfDob} !max-w-[170px] md:!max-w-[200px] !whitespace-normal !break-words`
                            : Styles.errorOfDob
                        }
                        outerWraperClass={
                          current?.brandSpecificParams?.isTrafficLightEnabled
                            ? "!max-w-[170px] md:!max-w-[200px]"
                            : undefined
                        }
                        onFocus={() => {
                          clearFieldTouched(`${index}_dateValue`);
                          const newIsEditingDob = [...isEditingDob];
                          newIsEditingDob[index] = true;
                          setIsEditingDob(newIsEditingDob);
                        }}
                        onBlur={() => {
                          markFieldsAboveAsTouched(index, "dateValue");
                          const newIsEditingDob = [...isEditingDob];
                          newIsEditingDob[index] = false;
                          setIsEditingDob(newIsEditingDob);
                        }}
                        onChange={(date: Date | unknown) =>
                          handleDobDateChange(index, date)
                        }
                        brand={brand}
                      />

                      <div className="text-xs leading-[27px] font-semibold text-text-on-primary ml-3 md:ml-4 mt-4 min-w-[65px] sm:relative sm:right-0 whitespace-nowrap">
                        {`${t("age", "Age")}: ${
                          form.value4 && Number(form.value4) >= 0
                            ? form.value4
                            : ""
                        }`}
                      </div>
                    </InputValidationWrapper>
                  </button>

                  {(() => {
                    const errorMessage = getDateValidation(
                      form,
                      index,
                      travellerType || Individual,
                      organizer?.travelling || false,
                      coupleMinAge || "",
                      dobMinAge || "",
                      dobMaxAge || "",
                      ageParentFamily || "",
                      ageFamilyFullTimeEducation || "",
                      ageGroupUnderTwo || "",
                      ageSingleParentFamily || "",
                      ageSingleParentFullTimeEducation || "",
                      formData,
                      (current?.brandSpecificParams
                        ?.ageLimitOrganizer as AgeLimitProps) || {}
                    );
                    return errorMessage ? (
                      <div className="w-full">
                        <p className={`${Styles.dobErrorMessage}`}>
                          {errorMessage}
                        </p>
                      </div>
                    ) : null;
                  })()}
                </div>
              ) : (
                <div>
                  <div className=" flex flex-row items-center mt-2">
                    <TextFieldSts
                      activeHoverd={activeHoverd}
                      label={t("traveller_age_label", "Age")}
                      id={`traveller-age-${index}`}
                      width="78px"
                      name="value4"
                      pattern="[0-9]{1-3}"
                      state={ageErrors[index] ? "error" : undefined}
                      message={ageErrors[index]}
                      type="number"
                      inputMode="numeric"
                      value={form.value4}
                      // onChange={(value) => {
                      //   // const sanitizedValue = value.replace(/[^0-9]/g, "");
                      //   // handleInputChange(index, "value4", sanitizedValue);

                      // }}
                      onChange={(
                        value: string,
                        event?: React.FocusEvent<HTMLInputElement>
                      ) => {
                        if (event) {
                          const invalidCharacters = /[^0-9]/.test(value);
                          if (invalidCharacters) {
                            event.preventDefault();
                          } else {
                            const sanitizedValue = value.replace(/[^0-9]/g, "");
                            handleInputChange(index, "value4", sanitizedValue);
                          }
                        }
                      }}
                      brand={brand}
                      inputFieldClassName={`${Styles.dobManualEnterField}`}
                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (
                          e.key === "." ||
                          e.key === "," ||
                          e.key === "e" ||
                          e.key === "E" ||
                          ((e.key < "0" || e.key > "9") && e.key.length === 1)
                        ) {
                          e.preventDefault();
                        }
                      }}
                      onInput={(e: React.FormEvent<HTMLInputElement>) => {
                        const inputElement = e.target as HTMLInputElement;
                        if (inputElement.value.includes(".")) {
                          inputElement.value = inputElement.value.replace(
                            ".",
                            ""
                          );
                        }
                      }}
                      msgCustomStyles={{ width: "auto" }}
                    />
                  </div>
                </div>
              )}

              {/* <div className=" mt-spacing-s">
                <InlineInfoBox
                  id="traveler-form-inline-info-box"
                  content={
                    <div>
                      <p>
                        {form.isDateCalenter
                          ? "Don’t know their date of birth?"
                          : ""}
                      </p>
                      <button
                        id="inline-info-box-button"
                        type="button"
                        onClick={() => {
                          handleAgeButtonPressed(index);
                        }}
                        className="text-interactive-secondary-default underline font-semibold"
                      >
                        {form.isDateCalenter
                          ? "Enter age instead"
                          : "Enter date of birth instead"}
                      </button>
                    </div>
                  }
                  style={{
                    width: "100%",
                    backgroundColor: "#EDF2FF",
                    borderColor: "#006BD6",
                    padding: "8px 16px 8px 16px",
                    borderRadius: "8px",
                    color: "#272427",
                    fontSize: "16px",
                    lineHeight: "24px",
                  }}
                />
              </div> */}

              {/* Identification Fields (Spain only) - Hide for Spain hub variant */}
              {showIdentificationFields &&
                !personalDetailsHidden &&
                (() => {
                  const age = Number.parseInt(form.value4 || "0");
                  const hasAgeEntered = form.value4 && form.value4 !== "";
                  const isOptional = hasAgeEntered && age < 18 && age >= 0;
                  const typeBasePlaceholder =
                    identificationTypePlaceholder ||
                    "identificationTypePlaceholder";
                  const numberBasePlaceholder =
                    identificationNumberPlaceholder ||
                    "identificationNumberPlaceholder";

                  return (
                    <div className="mt-spacing-m">
                      {/* Identification Type Dropdown - Inlined */}
                      <InputValidationWrapper
                        className="flex flex-row mb-spacing-s"
                        status={identificationTypeStatuses[index]}
                        childRef={
                          errorDetails.firstError ===
                          `${index}_identificationType`
                            ? (firstErrorRef as React.MutableRefObject<HTMLDivElement | null>)
                            : undefined
                        }
                      >
                        <div
                          className={`${NationalityStyles.country_wrapper} ${
                            NationalityStyles.title_dropdown
                          } ${
                            isIdentificationDropdownFocused[index] &&
                            NationalityStyles.dropdown_focused
                          } flex flex-row items-start w-full h-[56px]`}
                          onFocus={identificationTypeFocusHandlers[index]}
                          onBlur={identificationTypeDropdownBlurHandlers[index]}
                          ref={(el) => {
                            identificationTypeRefs.current[index] = el;
                            if (
                              errorDetails.firstError ===
                              `${index}_identificationType`
                            ) {
                              firstErrorRef.current = el;
                            }
                          }}
                          role="tab"
                          tabIndex={-1}
                        >
                          <Dropdown
                            id={`identification-type-dropdown-${index + 1}`}
                            defaultValue={form.identificationType}
                            placeholderTextAndLabel={
                              isOptional
                                ? `${typeBasePlaceholder} (${t(
                                    "optional",
                                    "Optional"
                                  )})`
                                : typeBasePlaceholder
                            }
                            strokeColor={getStrokeColor(brand)}
                            onSelectionChange={
                              identificationTypeChangeHandlers[index]
                            }
                            onDropdownBlur={
                              identificationTypeBlurHandlers[index]
                            }
                            className={`${NationalityStyles.droplistBox} ${
                              identificationTypeStatuses[index] === "warning"
                                ? NationalityStyles.warningMsgClass
                                : ""
                            } ${
                              identificationTypeStatuses[index] === "success"
                                ? NationalityStyles.successMsgClass
                                : ""
                            } border-interactive-secondary-default rounded-radius-lg max-h-spacing-xxxl`}
                            placeholderLeftBeforeSelect={0}
                            placeholderLeftAfterSelect={-24}
                            listBoxStyle={{
                              outline: "none",
                              margin: "0",
                              minWidth: "unset !important",
                              boxShadow:
                                "0px 0px 12px 0px rgba(15, 23, 42, 0.11)",
                            }}
                            listClassName={`${NationalityStyles.listStyle} mt-[10px] rounded-radius-s`}
                            unorderedListClassName="w-full !mt-[3px] !md:ml-0 !ml-[-1px] !border-border-subdued !rounded-radius-lg !px-spacing-m !py-spacing-xs"
                          >
                            {filteredIdentificationTypes[index]?.map((type) => (
                              <DropdownItem value={type.id} key={type.id}>
                                <span className="w-full leading-[24px] font-proximanova text-2xs font-semibold p-spacing-xs">
                                  {type.label}
                                </span>
                              </DropdownItem>
                            ))}
                          </Dropdown>
                        </div>
                      </InputValidationWrapper>

                      {/* Identification Number Field - Inlined */}
                      <button
                        type="button"
                        className="w-full focus-visible:outline-none"
                        tabIndex={-1}
                        ref={(el) => {
                          identificationNumberRefs.current[index] = el;
                          if (
                            errorDetails.firstError ===
                            `${index}_identificationNumber`
                          ) {
                            firstErrorRef.current = el;
                          }
                        }}
                      >
                        <InputValidationWrapper
                          className={`${NationalityStyles.input_wrapper} flex flex-row items-start mb-spacing-s`}
                          status={identificationNumberStatuses[index]}
                          childRef={
                            errorDetails.firstError ===
                            `${index}_identificationNumber`
                              ? (firstErrorRef as React.MutableRefObject<HTMLDivElement | null>)
                              : undefined
                          }
                        >
                          <TextFieldSts
                            label={
                              isOptional
                                ? `${numberBasePlaceholder} (${t(
                                    "optional",
                                    "Optional"
                                  )})`
                                : numberBasePlaceholder
                            }
                            data-testid={`identification-number-traveller-${
                              index + 1
                            }`}
                            id={`identification-number-traveller-${index + 1}`}
                            width="100%"
                            name="identificationNumber"
                            maxLength={12}
                            state={
                              identificationNumberStatuses[index] ??
                              (identificationNumberErrors[index]
                                ? "error"
                                : undefined)
                            }
                            message={identificationNumberErrors[index] || ""}
                            value={form.identificationNumber || ""}
                            onChange={identificationNumberChangeHandlers[index]}
                            onBlur={identificationNumberBlurHandlers[index]}
                            msgCustomStyles={{ width: "auto" }}
                            brand={brand}
                          />
                        </InputValidationWrapper>
                      </button>
                    </div>
                  );
                })()}

              {shouldShowEducationQuestion(form) && (
                <div
                  className="md:mt-spacing-l mt-spacing-m text-left"
                  ref={(el) => {
                    fullTimeEducationRefs.current[index] = el;
                    if (
                      errorDetails.firstError === `${index}_fullTimeEducation`
                    ) {
                      firstErrorRef.current = el;
                    }
                  }}
                >
                  <h2 className=" font-header-font md:text-3xl text-[26px] md:leading-[34.1px] leading-[29.9px] text-text-title text-left md:mb-spacing-l mb-spacing-m">
                    {t(
                      "traveller_full_time_education_question",
                      "Is this traveller in full-time education?"
                    )}
                  </h2>
                  <div className={`${Styles.traveller_btn_wrapper}`}>
                    <ButtonGroup
                      setSelectedAnswer={(answer: number) => {
                        handleFullTimeEducation(index, answer);
                      }}
                      selectedAnswer={
                        findAnswerQThreeByValue(form.fullTimeEducation)?.id
                      }
                      answerArray={translatedAnswersQuestionThree}
                      location={QuestionThree}
                      type="traveller-from-education"
                      onOptionClick={() => {
                        let targetElement: HTMLElement | null = null;

                        if (index + 1 === formData.length) {
                          const addBtnElement = document.getElementById(
                            "loadmoreButton-automate-test-id-8"
                          );
                          targetElement = addBtnElement
                            ? addTravellerRef.current
                            : document.getElementById(
                                "traveller-medical-answer-header-id"
                              );
                        } else {
                          targetElement = inputRefs.current[index + 1] ?? null;
                        }

                        if (targetElement) {
                          setTimeout(() => {
                            targetElement?.scrollIntoView({
                              behavior: "smooth",
                              block:
                                index + 1 === formData.length
                                  ? "center"
                                  : "start",
                            });
                          }, 100);
                        }
                      }}
                    />
                  </div>
                </div>
              )}

              {shouldShowRemoveButtonFamily(index) && (
                <div className=" remove-button text-xs leading-[18px] font-semibold">
                  <RemoveButton
                    hierarchy="tertiary"
                    id={`removeButton-${index}`}
                    label={removeButtonLabel}
                    onClick={() => {
                      setIsModalOpen(true);
                      setRemoveIndex(index);
                    }}
                  />
                </div>
              )}
              {shouldShowRemoveButtonOther(index) && (
                <div className="remove-button text-xs leading-[18px] font-semibold">
                  <RemoveButton
                    hierarchy="tertiary"
                    id={`removeButton-${index}`}
                    label={removeButtonLabel}
                    onClick={() => {
                      setIsModalOpen(true);
                      setRemoveIndex(index);
                    }}
                  />
                </div>
              )}
            </div>
          ))}
          {!(multipleForm && formData && formData.length < travelersLimit) && (
            <div data-testid="paddinDiv" className="md:pb-[0] pb-spacing-xs" />
          )}

          {multipleForm && formData && formData.length < travelersLimit && (
            <div className=" add-more-btn-wrapper" ref={addTravellerRef}>
              <AddButton
                data-testid="loadmoreButton-wrapper-6"
                hierarchy="secondary"
                id={`loadmoreButton-automate-test-id-${formData.length}`}
                label={addButtonLabel}
                onClick={() => {
                  setTimeout(() => {
                    inputRefs.current[formData.length]?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }, 100);

                  handleAddForm();
                }}
                isRightIcon={false}
                className=" bg-surface-neutral-option-1 text-xs leading-[18px] 
              font-semibold md:w-[247px] w-full focus:border-interactive-secondary-default"
              />
            </div>
          )}

          {newMedicalFlowEnabled && (
            <TravellerMedicalAnswer
              show
              questionText={medicalQuestionText}
              questionInfoText={medicalQuestionInfoText}
              serviceDeniedText={medicalServiceDeniedText}
              yesBtnText={medicalYesBtnText}
              noBtnText={medicalNoBtnText}
              location={QuestionThree}
              selectedValue={hasMedicalConditions}
              onChange={handleMedicalQuestion}
              isScrollToBottom={scrollMedicalQuestion}
              type="traveller"
            />
          )}

          {showErrors &&
            errorDetails.count > 0 &&
            current?.brandSpecificParams?.isTrafficLightEnabled &&
            hasMedicalConditions !== true && (
              <div className="mt-spacing-l">
                <InlineInfoBox
                  id="traveler-form-inline-info-box"
                  content={
                    <p className="text-text-info-1-primary">
                      {getAlertMessageIncompleteSection()}
                      &nbsp;
                      <button
                        type="button"
                        className="underline font-bold leading-[24px]"
                        onClick={() => {
                          const el = firstErrorRef?.current as Element | null;
                          if (el) {
                            const topOffset =
                              el.getBoundingClientRect().top +
                              window.scrollY -
                              20;
                            window.scrollTo({
                              top: topOffset,
                              behavior: "smooth",
                            });
                          }
                        }}
                      >
                        {getAlertMessageLinkSection()}
                      </button>
                    </p>
                  }
                  style={{
                    width: "100%",
                    backgroundColor: "#FFF0E9",
                    borderColor: "#D06D10",
                    padding: "8px 16px 8px 16px",
                    borderRadius: "8px",
                    color: "#272427",
                    fontSize: "16px",
                    lineHeight: "24px",
                  }}
                />
              </div>
            )}
        </div>
        {/* <button type="submit">Submit</button> */}

        <Modal
          className="traveller-remove-modal md:p-spacing-xxxl p-spacing-l md:max-w-[416px] max-w-[327px] w-full"
          isOpen={isModalOpen}
          onOpenChange={(e: boolean) => setIsModalOpen(e)}
          underlayStyles={{
            zIndex: 9999,
            background: "rgba(26, 26, 26, 0.6)",
          }}
          modalStyles={{
            display: "flex",
            background: "white",
            position: "fixed",
            top: "30%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            borderRadius: 8,
          }}
        >
          <div>
            <h1 className="text-text-title font-header-font md:text-3xl text-[23px] md:leading-[34.1px] leading-[27px] mb-spacing-m">
              {popupMessage}
            </h1>
            <div className="flex md:flex-row flex-col">
              <TextButton
                id={`traveler-remove-yes-button-${removeIndex}`}
                hierarchy="primary"
                label={t("confirmation_yes", "Yes")}
                onClick={() => {
                  if (removeIndex !== null) {
                    inputRefs.current[removeIndex - 1]?.scrollIntoView({
                      behavior: "smooth",

                      block: "start",
                    });
                    handleRemoveForm();
                    setIsModalOpen(false);
                    setRemoveIndex(null);
                  }
                }}
                className="md:mr-2 md:mb-0 mb-2 !h-[48px] md:w-[148px] w-full"
              />

              <TextButton
                id={`traveler-remove-cancel-button-${removeIndex}`}
                hierarchy="secondary"
                label={t("confirmation_cancel", "Cancel")}
                onClick={() => {
                  if (removeIndex !== null) {
                    inputRefs.current[removeIndex]?.scrollIntoView({
                      behavior: "smooth",

                      block: "start",
                    });
                    setIsModalOpen(false);
                    setRemoveIndex(null);
                  }
                }}
                isLeftIcon={false}
                className=" !h-[48px] md:w-[148px] w-full"
              />
            </div>
          </div>
        </Modal>
      </form>
    </div>
  );
}

export default withBrand(QSixForm);
